/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import React, {
    FunctionComponent,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { OfferType } from 'client-code/wrs-feed-service';

import { TIVO_SERVICES } from '../../config';
import { DeviceModel } from '../../core/device';
import { ServicesClientImpl } from '../../core/interactors/ServicesClientImpl';
import { Log } from '../../core/logging/Log';
import { KeyManager } from '../../core/navigation';
import { CursorManager } from '../../core/navigation/CursorManager';
import { LocationUrls } from '../../core/navigation/LocationUrls';
import { ScreenControllerTypes } from '../../core/navigation/ScreenControllerTypes';
import { ScreenTaskControllerTypes } from '../../core/navigation/ScreenTaskControllerTypes';
import { TScreen } from '../../core/navigation/types';
import { NetworkManager } from '../../core/network/NetworkManager';
import { PlaybackContentType } from '../../core/playback/enums';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { UIOrchestrator, UIOrchestratorState } from '../../core/uiorchestrator';
import { PlayerViewMode } from '../../core/videoplayer/enums';
import { IPlayable } from '../../core/videoplayer/interfaces/IPlayable';
import { VideoPlaybackController } from '../../core/videoplayer/VideoPlaybackController';
import {
    resetHomeScreenAction,
    setHomeScreenPreviewAction,
} from '../../store/homescreen/homescreen.actions';
import {
    checkAndLoadMoreCarousels,
    fetchHomeScreenNavItems,
    fetchPreviewForFocusedTile,
    getSingleChannelInfo,
    loadHomeScreenData,
    refreshHomeScreenData,
} from '../../store/homescreen/homescreen.reducers';
import {
    clearOverlayTaskController,
    hideOverlayDialog,
} from '../../store/overlaydialog/overlaydialog.reducers';
import { getRecordingsStatus } from '../../store/recordings/recordings.reducers';
import { setScreenTaskController } from '../../store/screens/screens.reducers';
import { hideSpinner } from '../../store/spinner/spinner.reducers';
import { VisibilityState } from '../../store/visibilityevents/visibilitystate';
import { AntiburnOsd } from '../common';
import { useScreenController } from '../hooks';
import { NoNetworkOverlay } from '../NoNetworkOverlay';
import { Whisper } from '../Whisper';
import CarouselComponent from './Carousel/Carousel';
import CarouselSkeleton from './Carousel/CarouselSkeleton';
import InteractiveLayer from './InteractiveLayer/InteractiveLayer';
import TopNavBar from './TopNavBar/TopNavBar';

import './HomeScreen.scss';

const TAG = 'HomeScreen';
const HOME_SCREEN_VIDEO_WINDOW_DEBOUNCE_MS = 3000;
const HOME_SCREEN_VIDEO_WINDOW_CAROUSEL_NAME = 'kaveriRecentlyWatched'; // Name of the carousel to have home-screen video-window playback (live tile preview in highlight)

const HomeScreen: FunctionComponent = (): JSX.Element => {
    useScreenController<TScreen>({
        identifier: TAG,
        properties: {
            url: LocationUrls.HOME_SCREEN,
            screenController: ScreenControllerTypes.HOME_SCREEN,
            taskController: ScreenTaskControllerTypes.DEFAULT_HOME_SCREEN_TASKS,
        },
    });

    const dispatch = useTivoDispatch();

    const homeScreen = useTivoSelector((state: RootState) => state.homeScreen);
    const { visibilityState } = useTivoSelector((state: RootState) => state.visibilityEvents);
    const isOverlayDialogActive = useTivoSelector(
        (state: RootState) => state.overlayDialog.isOverlayDialogActive,
    );
    const activeInteractiveComponent = useTivoSelector(
        (state: RootState) => state.interactiveLayer.activeComponent,
    );

    const isAntiburnOsdActive = useTivoSelector((state: RootState) => state.antiburnOsd.isActive);

    const {
        carousels,
        focusState,
        navBarFocused,
        navBarIndex,
        navItems,
        ghostState,
        previewData,
        isLoading,
        isLoadingMoreCarousels,
        isError,
        verticalScrollIndex,
        refreshIntervalSecs,
        pageCursor,
        lastFetchTimestamp,
    } = homeScreen;

    const imageHostBaseUrl = useMemo(
        () => ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost] ?? '',
        [],
    );

    const highlightFrameRef = useRef<HTMLDivElement | null>(null);
    const hsVideoWindowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isHsVideoWindowActiveRef = useRef(false);
    const hasMountedRef = useRef(false);
    const previewFetchedForCarouselRef = useRef(-1);

    const [isHsVideoWindowPlaying, setIsHsVideoWindowPlaying] = useState(false);

    /** Callback passed to the featured Carousel to capture the highlight frame element. */
    const handleHighlightFrameRef = useCallback((el: HTMLDivElement | null) => {
        highlightFrameRef.current = el;
    }, []);

    // Mount: Load data, hide PIG, ensure key ignoring is stopped
    useEffect(() => {
        Log.i(TAG, 'HomeScreen mounted.');

        const vpc = VideoPlaybackController.getInstance();
        const isVideoWindowEnabled = DeviceModel.getAppSettings().isVideoWindowEnabled();

        dispatch(hideSpinner());
        if (isOverlayDialogActive) {
            dispatch(hideOverlayDialog());
            dispatch(clearOverlayTaskController());
            dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_HOME_SCREEN_TASKS));
        }

        // Unmount the video window — playback is fully torn down while on HomeScreen.
        // The only video playback on HomeScreen is the home-screen video-window tile, which manages
        // its own lifecycle via startPlayback() + homeScreenVideoWindow().
        if (isVideoWindowEnabled) {
            vpc.unmount();
        }

        dispatch(loadHomeScreenData());
        dispatch(fetchHomeScreenNavItems());
        dispatch(getRecordingsStatus());

        const handleRecordingStatusUpdate = (state: UIOrchestratorState) => {
            switch (state) {
                case UIOrchestratorState.RECORDING_CREATED:
                case UIOrchestratorState.RECORDING_DELETED:
                case UIOrchestratorState.RECORDING_RECOVERED:
                case UIOrchestratorState.ONE_PASS_CREATED:
                case UIOrchestratorState.ONE_PASS_DELETED:
                case UIOrchestratorState.ONE_PASS_MODIFIED:
                    dispatch(getRecordingsStatus());
                    break;
                default:
            }
        };
        UIOrchestrator.stateChangeListener.subscribe(handleRecordingStatusUpdate);

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        // Subscribe to network state changes: when network reconnects, reload
        // home screen data. NoNetworkOverlay handles the disconnect UI.
        const onNetworkStateChange = () => {
            if (!NetworkManager.isNetworkDisconnected()) {
                dispatch(loadHomeScreenData());
            }
        };
        const networkManagerRef = NetworkManager.subscribe(onNetworkStateChange);

        return () => {
            NetworkManager.unsubscribe(networkManagerRef);
            // Cancel any home-screen video-window debounce timer
            if (hsVideoWindowTimerRef.current) {
                clearTimeout(hsVideoWindowTimerRef.current);
                hsVideoWindowTimerRef.current = null;
            }
            // Prevent the home-screen video-window effect cleanup from re-unmounting the player
            isHsVideoWindowActiveRef.current = false;

            UIOrchestrator.stateChangeListener.cancel(handleRecordingStatusUpdate);

            if (isVideoWindowEnabled) {
                // If the user pressed OK (full-screen), mode is FullScreen — don't interfere.
                // Otherwise, resume PIG by tuning to the last watched channel.
                if (vpc.getPlayerViewMode() !== PlayerViewMode.FullScreen) {
                    vpc.resumeFromHomeScreen();
                }
            }
            dispatch(setHomeScreenPreviewAction(null));
            dispatch(resetHomeScreenAction());
            Log.i(TAG, 'HomeScreen unmounted.');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Visibility change: refresh data when returning to foreground
    // Guard against the initial render: visibilityState starts as ON_RESUME in the
    // Redux store, which would otherwise trigger a redundant fetch on mount.
    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }
        if (visibilityState === VisibilityState.ON_RESUME) {
            dispatch(refreshHomeScreenData());
            dispatch(getRecordingsStatus());
        }
    }, [visibilityState, dispatch]);

    // Program-boundary refresh: each carousel carries a refreshIntervalSecs indicating
    // when the current program ends. Fire a one-shot timer at the earliest boundary,
    // refresh, then the new response resets the intervals — chaining to the next boundary.
    // Only active while the app is in the foreground.
    useEffect(() => {
        if (visibilityState !== VisibilityState.ON_RESUME || refreshIntervalSecs <= 0) {
            return undefined;
        }

        const timerId = setTimeout(() => {
            dispatch(refreshHomeScreenData(true /* isProgramBoundaryRefresh */));
        }, refreshIntervalSecs * 1000);

        return () => clearTimeout(timerId);
    }, [visibilityState, refreshIntervalSecs, lastFetchTimestamp, dispatch]);

    // Fetch preview when the focused carousel first becomes available (initial load,
    // after a refresh, or after pagination loads the focused carousel into range).
    // Tracks which carousel index preview was last fetched for so it re-fires if
    // pagination brings a previously out-of-range focused carousel into view.
    // Must NOT re-fire for carousels beyond the focused one during pagination.
    useEffect(() => {
        if (carousels.length === 0) {
            previewFetchedForCarouselRef.current = -1;
            return;
        }
        const isFocusedCarouselLoaded = focusState.carouselIndex < carousels.length;
        if (
            !navBarFocused &&
            isFocusedCarouselLoaded &&
            previewFetchedForCarouselRef.current !== focusState.carouselIndex
        ) {
            previewFetchedForCarouselRef.current = focusState.carouselIndex;
            dispatch(fetchPreviewForFocusedTile());
        }
        dispatch(checkAndLoadMoreCarousels());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carousels.length]);

    // ── Home-screen video-window tile playback (live preview inside the tile highlight) ───
    useEffect(() => {
        if (!DeviceModel.getAppSettings().isVideoWindowEnabled()) return undefined;

        // Cancel any running debounce timer
        if (hsVideoWindowTimerRef.current) {
            clearTimeout(hsVideoWindowTimerRef.current);
            hsVideoWindowTimerRef.current = null;
        }

        // Unmount home-screen video-window if it was active
        if (isHsVideoWindowActiveRef.current) {
            VideoPlaybackController.getInstance().unmount();
            isHsVideoWindowActiveRef.current = false;
            setIsHsVideoWindowPlaying(false);
        }

        // Only start when the focused carousel is the configured video-window carousel
        const focusedCarousel = carousels[focusState.carouselIndex];
        const shouldStartVideoWindow =
            focusedCarousel?.carouselName === HOME_SCREEN_VIDEO_WINDOW_CAROUSEL_NAME &&
            !navBarFocused;

        if (shouldStartVideoWindow) {
            const carousel = focusedCarousel;
            const feedItems = carousel?.feedItems?.feedItems ?? [];
            const feedItem = feedItems[focusState.tileIndex];

            // Tile must be live content with a stationId
            if (feedItem?.stationId && feedItem.offerType === OfferType.Live) {
                hsVideoWindowTimerRef.current = setTimeout(() => {
                    hsVideoWindowTimerRef.current = null;

                    const channel = getSingleChannelInfo(feedItem.stationId!);
                    if (!channel) return;

                    const livePlayable: IPlayable = {
                        channel,
                        playbackContentType: PlaybackContentType.LIVE,
                    };

                    const vpc = VideoPlaybackController.getInstance();
                    vpc.startPlayback(livePlayable);
                    vpc.homeScreenVideoWindow();
                    isHsVideoWindowActiveRef.current = true;
                    setIsHsVideoWindowPlaying(true);

                    Log.i(
                        TAG,
                        `Home-screen video-window playback started for stationId: ${feedItem.stationId}`,
                    );
                }, HOME_SCREEN_VIDEO_WINDOW_DEBOUNCE_MS);
            }
        }

        return () => {
            if (hsVideoWindowTimerRef.current) {
                clearTimeout(hsVideoWindowTimerRef.current);
                hsVideoWindowTimerRef.current = null;
            }
            // Unmount the old video-window immediately when focus moves away,
            // so it doesn't linger while the debounce for the new tile is running.
            if (isHsVideoWindowActiveRef.current) {
                const vpc = VideoPlaybackController.getInstance();
                vpc.unmount();
                isHsVideoWindowActiveRef.current = false;
                setIsHsVideoWindowPlaying(false);
                Log.i(TAG, 'Home-screen video-window playback unmounted (focus moved away)');
            }
        };
    }, [focusState.carouselIndex, focusState.tileIndex, navBarFocused, carousels]);

    useEffect(() => {
        if (!DeviceModel.getAppSettings().isVideoWindowEnabled()) return undefined;

        const vpc = VideoPlaybackController.getInstance();

        const handlePlaybackError = () => {
            if (isHsVideoWindowActiveRef.current) {
                Log.i(TAG, 'Playback error on home-screen video-window — falling back to poster.');
                vpc.unmount();
                isHsVideoWindowActiveRef.current = false;
                setIsHsVideoWindowPlaying(false);
            }
        };

        vpc.getPlaybackErrorListener().subscribe(handlePlaybackError);

        return () => {
            vpc.getPlaybackErrorListener().cancel(handlePlaybackError);
        };
    }, []);

    // Scroll focused carousel into view using translateY on the carousel list.
    // verticalScrollIndex drives the carousel list position so the highlight stays
    // fixed on screen. Each carousel row's height is measured dynamically.
    const contentRef = useRef<HTMLDivElement>(null);
    const carouselRefs = useRef<(HTMLDivElement | null)[]>([]);
    const carouselListRef = useRef<HTMLDivElement>(null);
    const needsSnapRef = useRef(true);

    useLayoutEffect(() => {
        const listEl = carouselListRef.current;
        if (!listEl) return;

        if (navBarFocused) {
            listEl.style.transform = 'translateY(0)';
            return;
        }

        if (carousels.length === 0) {
            listEl.style.transform = 'translateY(0)';
            needsSnapRef.current = true;
            return;
        }

        const targetRow = carouselRefs.current[verticalScrollIndex];
        const offset = targetRow?.offsetTop ?? 0;

        const transformValue = `translateY(-${offset}px)`;
        if (needsSnapRef.current) {
            listEl.classList.add('carousel-list--no-transition');
            listEl.style.transform = transformValue;
            listEl.offsetHeight;
            listEl.classList.remove('carousel-list--no-transition');
            needsSnapRef.current = false;
        } else {
            listEl.style.transform = transformValue;
        }
    }, [verticalScrollIndex, navBarFocused, carousels.length]);

    return (
        <div className="home-screen">
            {!(isLoading && carousels.length === 0) && <NoNetworkOverlay />}
            <div className="body">
                <TopNavBar
                    navBarFocused={navBarFocused}
                    navBarIndex={navBarIndex}
                    navItems={navItems}
                    ghostNavBarIndex={ghostState.navBarIndex}
                />

                <div className="content" ref={contentRef}>
                    {isError && carousels.length === 0 && <div className="empty" />}

                    <div className="carousel-list" ref={carouselListRef}>
                        {/* Initial load skeleton — shown before first batch of carousels arrives */}
                        {isLoading && carousels.length === 0 && <CarouselSkeleton count={3} />}

                        {carousels.map((carousel, index) => (
                            <div
                                key={carousel.carouselName ?? index}
                                className="carousel-list-row"
                                ref={(el) => {
                                    carouselRefs.current[index] = el;
                                }}
                            >
                                <CarouselComponent
                                    carousel={carousel}
                                    carouselIndex={index}
                                    focusedCarouselIndex={focusState.carouselIndex}
                                    focusedTileIndex={focusState.tileIndex}
                                    previewData={
                                        index === focusState.carouselIndex && !navBarFocused
                                            ? previewData
                                            : null
                                    }
                                    imageHostBaseUrl={imageHostBaseUrl}
                                    navBarFocused={navBarFocused}
                                    ghostCarouselIndex={ghostState.carouselIndex}
                                    ghostTileIndex={ghostState.tileIndex}
                                    ghostScrollOffset={ghostState.scrollOffset}
                                    isGhostTarget={ghostState.carouselIndex === index}
                                    activeInteractiveComponent={activeInteractiveComponent}
                                    onHighlightFrameRef={
                                        carousel.carouselName ===
                                        HOME_SCREEN_VIDEO_WINDOW_CAROUSEL_NAME
                                            ? handleHighlightFrameRef
                                            : undefined
                                    }
                                    isHomeScreenVideoWindowPlaying={
                                        carousel.carouselName ===
                                        HOME_SCREEN_VIDEO_WINDOW_CAROUSEL_NAME
                                            ? isHsVideoWindowPlaying
                                            : false
                                    }
                                    previousCarouselTitle={
                                        index > 0 && verticalScrollIndex >= index
                                            ? carousels[index - 1]?.caption
                                            : undefined
                                    }
                                />
                            </div>
                        ))}

                        {/* Pagination skeleton — shown while loadMoreCarousels (pageCursor) is in-flight */}
                        {isLoadingMoreCarousels && pageCursor && <CarouselSkeleton count={2} />}
                    </div>
                </div>
            </div>

            <div className="gradient-footer" />

            <Whisper message="" />

            {DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared() ? (
                <InteractiveLayer />
            ) : null}
            {isAntiburnOsdActive && <AntiburnOsd parentalCheck={false} />}
        </div>
    );
};

export default HomeScreen;
